/**
 * Shared session-id capture utility.
 *
 * The WorkOS logout endpoint requires a session_id.
 * AuthKit's signOut() can normally supply it from in-memory state, but once
 * tokens are cleared from localStorage and the page navigates, that in-memory
 * state is gone.  The only safe moment to capture the sid is immediately before
 * localStorage is wiped.  Call captureSessionId() at every code path that does
 * localStorage.removeItem('access_token').
 */

const SESSION_ID_KEY = 'pending_logout_session_id'

/** Extract the WorkOS session_id (sid claim) from the stored JWT and persist
 *  it in sessionStorage so the logout pages can use it later. */
export function captureSessionId(): void {
  const token = localStorage.getItem('access_token')
  console.debug('[auth:session] captureSessionId called', {
    hasToken: !!token,
    existing: sessionStorage.getItem(SESSION_ID_KEY),
  })

  if (!token) {
    console.warn('[auth:session] captureSessionId: no access_token in localStorage — session_id not captured')
    return
  }

  try {
    const parts = token.split('.')
    if (parts.length !== 3) throw new Error('not a JWT')
    const payload = JSON.parse(atob(parts[1]))
    const sid: string | undefined = payload.sid
    if (sid) {
      sessionStorage.setItem(SESSION_ID_KEY, sid)
      console.debug('[auth:session] session_id captured', { sid })
    } else {
      console.warn('[auth:session] captureSessionId: JWT has no sid claim', { payload })
    }
  } catch (err) {
    console.error('[auth:session] captureSessionId: failed to decode JWT', err)
  }
}

/** Return the stored session_id and remove it from sessionStorage. */
export function consumeSessionId(): string | null {
  const sid = sessionStorage.getItem(SESSION_ID_KEY)
  sessionStorage.removeItem(SESSION_ID_KEY)
  console.debug('[auth:session] consumeSessionId', { sid })
  return sid
}

const INTENDED_LOCATION_KEY = 'intendedLocation'

/** Save the URL the user was trying to access before being redirected to login. */
export function captureIntendedLocation(path: string): void {
  // Don't save auth-related paths — they're not real destinations
  if (path.startsWith('/auth')) return
  sessionStorage.setItem(INTENDED_LOCATION_KEY, path)
}

/** Return the stored intended location and remove it from sessionStorage. */
export function consumeIntendedLocation(): string | null {
  const location = sessionStorage.getItem(INTENDED_LOCATION_KEY)
  sessionStorage.removeItem(INTENDED_LOCATION_KEY)
  return location
}

/** Dump the full auth-related storage state to the console — useful for debugging. */
export function debugDumpAuthState(label: string): void {
  console.group(`[auth:debug] ${label}`)
  console.log('localStorage', {
    access_token: localStorage.getItem('access_token') ? '<present>' : null,
    refresh_token: localStorage.getItem('refresh_token') ? '<present>' : null,
  })
  console.log('sessionStorage', {
    pending_logout_session_id: sessionStorage.getItem('pending_logout_session_id'),
    post_logout_action: sessionStorage.getItem('post_logout_action'),
    logout_reason: sessionStorage.getItem('logout_reason'),
    logout_message: sessionStorage.getItem('logout_message'),
    intentional_logout: sessionStorage.getItem('intentional_logout'),
  })
  console.log('cookies (readable)', document.cookie || '(none)')
  console.groupEnd()
}
